"use client"

import {useEffect, useState, type FormEvent} from "react"
import {useRouter} from "next/navigation"
import Link from "next/link"

import {
    Eye,
    EyeOff,
    Loader2,
    TrendingUp,
    Wallet,
    BarChart3,
    Shield,
} from "lucide-react"
import {useAppDispatch, useAppSelector} from "@/src/store/hooks";
import {loginUser} from "@/src/store/slices/authSlice";
import {getRoleHomePath} from "@/src/lib/roles";
import {Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle} from "@/src/components/ui/card";
import {Label} from "@/src/components/ui/label";
import {Input} from "@/src/components/ui/input";
import {Checkbox} from "@/src/components/ui/checkbox";
import {Button} from "@/src/components/ui/button";
import {Separator} from "@/src/components/ui/separator";

export default function LoginPage() {
    const router = useRouter()
    const dispatch = useAppDispatch()
    const {isLoading, error: apiError, user, isHydrated} = useAppSelector((state) => state.auth)

    // Already signed in (restored session, or a stale tab still on /login) —
    // skip the form and go straight to their dashboard.
    useEffect(() => {
        if (isHydrated && user) {
            router.replace(getRoleHomePath(user.role))
        }
    }, [isHydrated, user, router])

    const [formData, setFormData] = useState({
        email: "",
        password: "",
        rememberMe: false,
    })
    const [showPassword, setShowPassword] = useState(false)
    const [touched, setTouched] = useState<Record<string, boolean>>({})

    const errors: Record<string, string> = {}

    if (touched.email && !formData.email) {
        errors.email = "Email is required"
    } else if (
        touched.email &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
    ) {
        errors.email = "Invalid email format"
    }

    if (touched.password && !formData.password) {
        errors.password = "Password is required"
    } else if (touched.password && formData.password.length < 6) {
        errors.password = "Min 6 characters"
    }

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault()

        // Inline validation — don't rely on render-time `errors`
        setTouched({email: true, password: true})

        const emailError = !formData.email
            ? "Email is required"
            : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
                ? "Invalid email format"
                : ""
        const passwordError = !formData.password
            ? "Password is required"
            : formData.password.length < 6
                ? "Min 6 characters"
                : ""

        if (emailError || passwordError) return

        const result = await dispatch(
            loginUser({email: formData.email, password: formData.password})
        )

        if (loginUser.fulfilled.match(result)) {
            router.push(getRoleHomePath(result.payload.user.role))
        }
    }

    return (
        <div className="flex min-h-screen flex-col lg:flex-row">
            {/* ── Hero Side ── */}
            <div
                className="relative flex flex-1 items-center justify-center overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-900 p-8 lg:p-16">
                {/* Animated background orbs */}
                <div className="pointer-events-none absolute inset-0 overflow-hidden">
                    <div
                        className="absolute -left-40 -top-40 h-80 w-80 animate-float rounded-full bg-white/5 blur-3xl"/>
                    <div
                        className="absolute -right-40 bottom-20 h-96 w-96 animate-float rounded-full bg-white/5 blur-3xl"
                        style={{animationDelay: "2s"}}
                    />
                    <div
                        className="absolute left-1/3 top-1/3 h-64 w-64 animate-float rounded-full bg-white/5 blur-3xl"
                        style={{animationDelay: "4s"}}
                    />
                    {/* Grid overlay */}
                    <div
                        className="absolute inset-0 opacity-[0.03]"
                        style={{
                            backgroundImage:
                                "linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)",
                            backgroundSize: "60px 60px",
                        }}
                    />
                </div>

                <div className="relative z-10 max-w-lg animate-fade-in">
                    {/* Logo & brand */}
                    <div className="mb-8 flex items-center gap-3">
                        <div
                            className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm">
                            <TrendingUp className="h-6 w-6 text-white"/>
                        </div>
                        <span className="text-2xl font-bold tracking-tight text-white">
              Cloud Ledger
            </span>
                    </div>

                    <h1 className="mb-4 text-4xl font-bold leading-tight text-white sm:text-5xl">
                        Intelligent Financial
                        <br/>
                        <span className="text-blue-200">Management</span>
                    </h1>
                    <p className="mb-10 text-lg text-blue-100/80">
                        Gain real-time insights, automate your workflows, and take control of
                        your business finances with our cloud-powered platform.
                    </p>

                    {/* Feature highlights */}
                    <div className="grid gap-4 sm:grid-cols-2">
                        {[
                            {
                                icon: BarChart3,
                                title: "Real-time Analytics",
                                desc: "Live dashboards with actionable financial insights",
                            },
                            {
                                icon: Wallet,
                                title: "Expense Tracking",
                                desc: "Automated categorization and smart budgeting",
                            },
                            {
                                icon: Shield,
                                title: "Enterprise Security",
                                desc: "Bank-grade encryption and access controls",
                            },
                            {
                                icon: TrendingUp,
                                title: "Growth Insights",
                                desc: "AI-powered forecasts and trend analysis",
                            },
                        ].map((feature) => (
                            <div
                                key={feature.title}
                                className="group flex items-start gap-3 rounded-xl bg-white/10 p-4 backdrop-blur-sm transition-all hover:bg-white/15"
                            >
                                <feature.icon className="mt-0.5 h-5 w-5 shrink-0 text-blue-200"/>
                                <div>
                                    <h3 className="text-sm font-semibold text-white">
                                        {feature.title}
                                    </h3>
                                    <p className="mt-0.5 text-xs text-blue-100/70">
                                        {feature.desc}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Trust indicators */}
                    <div className="mt-10 flex items-center gap-6 text-xs text-blue-200/60">
            <span className="flex items-center gap-1.5">
              <Shield className="h-3.5 w-3.5"/>
              SOC 2 Certified
            </span>
                        <span className="flex items-center gap-1.5">
              <Shield className="h-3.5 w-3.5"/>
              GDPR Compliant
            </span>
                        <span className="flex items-center gap-1.5">
              <Shield className="h-3.5 w-3.5"/>
              99.9% Uptime
            </span>
                    </div>
                </div>
            </div>

            {/* ── Form Side ── */}
            <div
                className="flex flex-1 items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 p-8 dark:from-slate-950 dark:to-blue-950/20 lg:p-16">
                <div className="w-full max-w-sm animate-slide-up">
                    <Card
                        className="border-0 bg-white/80 shadow-2xl shadow-blue-500/10 backdrop-blur-xl dark:bg-slate-900/80">
                        <CardHeader className="pb-4 text-center">
                            <div
                                className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 shadow-lg shadow-blue-500/25">
                                <TrendingUp className="h-7 w-7 text-white"/>
                            </div>
                            <CardTitle className="text-2xl font-bold tracking-tight">
                                Welcome back
                            </CardTitle>
                            <CardDescription className="text-sm">
                                Sign in to your Cloud Ledger account
                            </CardDescription>
                        </CardHeader>

                        <CardContent>
                            <form onSubmit={handleSubmit} noValidate>
                                {/* Global error banner — only shows after a failed API call */}
                                {apiError && (
                                    <div
                                        role="alert"
                                        className="mb-4 flex items-start gap-2 animate-slide-up rounded-lg border border-red-700/20 bg-red-500/10 p-3 text-sm text-red-500"
                                    >
                                        <svg
                                            className="mt-0.5 h-4 w-4 flex-shrink-0"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            strokeWidth={2}
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M12 9v3.75m0 3.75h.007M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                            />
                                        </svg>
                                        <span>{apiError}</span>
                                    </div>
                                )}

                                <div className="space-y-4">
                                    {/* Email */}
                                    <div className="space-y-2">
                                        <Label htmlFor="email">Email</Label>
                                        <div className="relative">
                                            <Input
                                                id="email"
                                                type="email"
                                                placeholder="you@company.com"
                                                autoComplete="email"
                                                autoFocus
                                                value={formData.email}
                                                onChange={(e) => {
                                                    setFormData((p) => ({...p, email: e.target.value}))
                                                    if (touched.email)
                                                        setTouched((p) => ({...p, email: true}))
                                                }}
                                                onBlur={() =>
                                                    setTouched((p) => ({...p, email: true}))
                                                }
                                                aria-invalid={!!errors.email}
                                                className="h-10 pl-3 pr-10"
                                            />
                                            {formData.email && !errors.email && (
                                                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                                    <div className="h-2 w-2 rounded-full bg-green-500"/>
                                                </div>
                                            )}
                                        </div>
                                        {errors.email && (
                                            <p className="text-xs text-red-500">{errors.email}</p>
                                        )}
                                    </div>

                                    {/* Password */}
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <Label htmlFor="password">Password</Label>
                                            <Link
                                                href="/forgot-password"
                                                className="text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
                                            >
                                                Forgot?
                                            </Link>
                                        </div>
                                        <div className="relative">
                                            <Input
                                                id="password"
                                                type={showPassword ? "text" : "password"}
                                                placeholder="Enter your password"
                                                autoComplete="current-password"
                                                value={formData.password}
                                                onChange={(e) => {
                                                    setFormData((p) => ({
                                                        ...p,
                                                        password: e.target.value,
                                                    }))
                                                    if (touched.password)
                                                        setTouched((p) => ({...p, password: true}))
                                                }}
                                                onBlur={() =>
                                                    setTouched((p) => ({...p, password: true}))
                                                }
                                                aria-invalid={!!errors.password}
                                                className="h-10 pl-3 pr-10"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                                tabIndex={-1}
                                                aria-label={
                                                    showPassword ? "Hide password" : "Show password"
                                                }
                                            >
                                                {showPassword ? (
                                                    <EyeOff className="h-4 w-4"/>
                                                ) : (
                                                    <Eye className="h-4 w-4"/>
                                                )}
                                            </button>
                                        </div>
                                        {errors.password && (
                                            <p className="text-xs text-red-500">{errors.password}</p>
                                        )}
                                    </div>

                                    {/* Remember me */}
                                    <div className="flex items-center gap-2">
                                        <Checkbox
                                            id="remember"
                                            checked={formData.rememberMe}
                                            onCheckedChange={(checked: boolean) =>
                                                setFormData((p) => ({
                                                    ...p,
                                                    rememberMe: checked as boolean,
                                                }))
                                            }
                                        />
                                        <Label
                                            htmlFor="remember"
                                            className="text-xs font-normal text-muted-foreground"
                                        >
                                            Remember me
                                        </Label>
                                    </div>
                                </div>

                                <Button
                                    type="submit"
                                    disabled={isLoading}
                                    className="mt-6 h-10 w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-sm font-semibold text-white shadow-lg shadow-blue-500/25 transition-all hover:from-blue-700 hover:to-indigo-700 hover:shadow-blue-500/40 active:scale-[0.98] disabled:opacity-60"
                                >
                                    {isLoading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin"/>
                                            Signing in...
                                        </>
                                    ) : (
                                        "Sign in"
                                    )}
                                </Button>
                            </form>
                        </CardContent>

                        <Separator className="mx-auto w-[calc(100%-2rem)]"/>

                        <CardFooter className="flex-col gap-3 pt-4">
                            <p className="text-xs text-muted-foreground">
                                Don&apos;t have an account?{" "}
                                <Link
                                    href="/register"
                                    className="font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
                                >
                                    Create one now
                                </Link>
                            </p>
                        </CardFooter>
                    </Card>

                    <p className="mt-6 text-center text-xs text-muted-foreground">
                        &copy; {new Date().getFullYear()} Cloud Ledger. All rights reserved.
                    </p>
                </div>
            </div>
        </div>
    )
}